import React, { useEffect, useState } from "react";
import {
  BuildingOfficeIcon,
  GlobeAltIcon,
  PlayIcon,
  CheckCircleIcon,
  ShieldExclamationIcon,
  CheckBadgeIcon,
  FireIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowPathIcon,
  CubeIcon,
} from "@heroicons/react/24/outline";
import { PageLoader } from "../../ui";
import VaptSummaryCard from "../components/VaptSummaryCard";
import VaptStatusBadge from "../components/VaptStatusBadge";
import VaptTable from "../components/VaptTable";
import {
  getVaptDashboardApi,
  listVaptScansApi,
  listVaptEnabledCustomersApi,
} from "../../../services/axios/AuthApis/VAPTApis";
import { VAPT_SEVERITIES, VAPT_DOMAINS } from "../utils/vaptConstants";
import { useAuth } from "../../../context/AuthContext";
import NotificationCenter from "../../ui/NotificationCenter";

const SEVERITY_BAR_COLOR = {
  Critical: "bg-red-500",
  High: "bg-orange-500",
  Medium: "bg-yellow-500",
  Low: "bg-green-500",
  Informational: "bg-blue-500",
};

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

const PLATFORM_ADMIN_ROLES = ["platform_admin", "admin"];

const VaptDashboard = ({ onNavigate }) => {
  const { user } = useAuth();
  const isPlatformAdmin = PLATFORM_ADMIN_ROLES.includes(user?.role);

  const [data, setData] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  // Platform Admin customer switcher: fetch the VAPT-ENABLED customer list
  // once (not every customer in the system - see listVaptEnabledCustomersApi),
  // only for platform_admin - regular customers never see or need this.
  useEffect(() => {
    if (!isPlatformAdmin) return;
    listVaptEnabledCustomersApi()
      .then((list) => setCustomers(list || []))
      .catch(() => setCustomers([]));
    // eslint-disable-next-line
  }, [isPlatformAdmin]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    // Note: recent-scans is not currently narrowed by the selected customer
    // (the scans list endpoint has no customer_id filter - only project_id/
    // status) - the summary cards/charts above are the ones that respect
    // the Platform Admin customer switcher.
    Promise.all([
      getVaptDashboardApi(selectedCustomerId || undefined),
      listVaptScansApi(),
    ])
      .then(([dashboard, scans]) => {
        if (!mounted) return;
        setData(dashboard);
        const scansList = asArray(scans)
          .slice()
          .sort(
            (a, b) => new Date(b.start_time || 0) - new Date(a.start_time || 0),
          )
          .slice(0, 6)
          .map((s) => ({ ...s, id: s.scan_id }));
        setRecentScans(scansList);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.message || "Failed to load dashboard data.");
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedCustomerId]);

  if (loading) return <PageLoader title="Loading VAPT dashboard…" />;

  if (error || !data) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">
          {error || "No dashboard data available."}
        </p>
      </div>
    );
  }

  const riskSummary = data.findings_by_severity || {};
  const domainSummary = data.domain_wise_findings || {};
  const maxRisk = Math.max(
    ...VAPT_SEVERITIES.map((s) => riskSummary[s] || 0),
    1,
  );
  const maxDomain = Math.max(
    ...VAPT_DOMAINS.map((d) => domainSummary[d] || 0),
    1,
  );

  return (
    <div className="space-y-6">
      {isPlatformAdmin && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-gray-500 uppercase">
              VAPT Enabled Customers
            </label>
            <select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm min-w-[220px]"
            >
              <option value="">All VAPT-Enabled Customers</option>
              {customers.map((c) => (
                <option
                  key={c.id || c.customer_id}
                  value={c.id || c.customer_id}
                >
                  {c.name || c.company_name || c.email || "Unnamed Customer"}
                </option>
              ))}
            </select>
          </div>
          {data.vapt_enabled_customers != null && (
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-gray-800">
                {data.vapt_enabled_customers}
              </span>{" "}
              VAPT-enabled customers
            </div>
          )}
        </div>
      )}

      {/* Asset Data — live, non-persisting Asset Manager snapshot (see
          vapt/services.py's get_asset_manager_snapshot), distinct from the
          VAPT Execution Data below which is derived from persisted
          VAPTProject/VulnerabilityScan/VAPTFinding rows. Omitted (null)
          when the effective customer doesn't have assetVaptEnabled, or for
          Platform Admin's "All Customers" global view. */}
      {data.asset_manager && (
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
            Asset Data — Asset Manager (Live)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <VaptSummaryCard
              label="Total Assets"
              value={data.asset_manager.total_assets}
              icon={CubeIcon}
              color="blue"
              delay={0}
            />
            <VaptSummaryCard
              label="Eligible Assets"
              value={data.asset_manager.eligible_assets}
              icon={CheckCircleIcon}
              color="green"
              delay={0.02}
            />
            <VaptSummaryCard
              label="Unsupported Assets"
              value={data.asset_manager.unsupported_assets}
              icon={ExclamationTriangleIcon}
              color="yellow"
              delay={0.04}
            />
            <VaptSummaryCard
              label="Out of Scope Assets"
              value={data.asset_manager.out_of_scope_assets}
              icon={ShieldExclamationIcon}
              color="gray"
              delay={0.06}
            />
          </div>
        </div>
      )}

      {/* VAPT Execution Data — computed from persisted VAPTProject/Scan/
          Finding records. */}
      <h3 className="text-xs font-semibold text-gray-500 uppercase">
        VAPT Execution Data
      </h3>
      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <div className="w-full lg:w-[28rem] flex-shrink-0 h-[380px]">
          <NotificationCenter module="vapt" title="VAPT Notifications" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 flex-1">
          <VaptSummaryCard
            label="Total Projects"
            value={data.total_projects}
            icon={BuildingOfficeIcon}
            color="blue"
            delay={0}
            onClick={() => onNavigate && onNavigate("projects")}
          />
          <VaptSummaryCard
            label="Asset Projects"
            value={data.asset_projects}
            icon={BuildingOfficeIcon}
            color="purple"
            delay={0.02}
            onClick={() => onNavigate && onNavigate("projects")}
          />
          <VaptSummaryCard
            label="External Projects"
            value={data.external_projects}
            icon={GlobeAltIcon}
            color="purple"
            delay={0.04}
            onClick={() => onNavigate && onNavigate("projects")}
          />
          <VaptSummaryCard
            label="Active Scans"
            value={data.active_scans}
            icon={PlayIcon}
            color="yellow"
            delay={0.06}
            onClick={() => onNavigate && onNavigate("scans")}
          />
          <VaptSummaryCard
            label="Completed Scans"
            value={data.completed_scans}
            icon={CheckCircleIcon}
            color="green"
            delay={0.08}
            onClick={() => onNavigate && onNavigate("scans")}
          />
          <VaptSummaryCard
            label="Open Findings"
            value={data.open_findings}
            icon={ShieldExclamationIcon}
            color="orange"
            delay={0.1}
            onClick={() => onNavigate && onNavigate("findings")}
          />
          <VaptSummaryCard
            label="Resolved Findings"
            value={data.resolved_findings}
            icon={CheckBadgeIcon}
            color="green"
            delay={0.12}
            onClick={() => onNavigate && onNavigate("findings")}
          />
          <VaptSummaryCard
            label="Pending Retests"
            value={data.pending_retests}
            icon={ClockIcon}
            color="gray"
            delay={0.14}
            onClick={() => onNavigate && onNavigate("findings")}
          />
          <VaptSummaryCard
            label="Critical Findings"
            value={riskSummary.Critical}
            icon={FireIcon}
            color="red"
            delay={0.16}
            onClick={() => onNavigate && onNavigate("findings")}
          />
          <VaptSummaryCard
            label="High Findings"
            value={riskSummary.High}
            icon={ExclamationTriangleIcon}
            color="orange"
            delay={0.18}
            onClick={() => onNavigate && onNavigate("findings")}
          />
          <VaptSummaryCard
            label="Medium Findings"
            value={riskSummary.Medium}
            icon={ExclamationTriangleIcon}
            color="yellow"
            delay={0.2}
            onClick={() => onNavigate && onNavigate("findings")}
          />
          <VaptSummaryCard
            label="Low Findings"
            value={riskSummary.Low}
            icon={ExclamationTriangleIcon}
            color="green"
            delay={0.22}
            onClick={() => onNavigate && onNavigate("findings")}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk summary */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Risk Summary by Severity
          </h3>
          <div className="space-y-3">
            {VAPT_SEVERITIES.map((sev) => (
              <div key={sev} className="flex items-center gap-3">
                <span className="w-24 text-xs text-gray-600">{sev}</span>
                <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${SEVERITY_BAR_COLOR[sev]}`}
                    style={{
                      width: `${((riskSummary[sev] || 0) / maxRisk) * 100}%`,
                    }}
                  />
                </div>
                <span className="w-8 text-xs font-semibold text-gray-700 text-right">
                  {riskSummary[sev] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Domain summary */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Findings by Domain
          </h3>
          <div className="space-y-3">
            {VAPT_DOMAINS.map((d) => (
              <div key={d} className="flex items-center gap-3">
                <span className="w-28 text-xs text-gray-600">{d}</span>
                <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary-500"
                    style={{
                      width: `${((domainSummary[d] || 0) / maxDomain) * 100}%`,
                      backgroundColor: "var(--app-primary-color, #26256b)",
                    }}
                  />
                </div>
                <span className="w-8 text-xs font-semibold text-gray-700 text-right">
                  {domainSummary[d] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent scans */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-800">Recent Scans</h3>
          <button
            onClick={() => onNavigate && onNavigate("scans")}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" /> View all scans
          </button>
        </div>
        <VaptTable
          columns={[
            { key: "scan_id", header: "Scan ID" },
            {
              key: "scan_name",
              header: "Scan Name",
              render: (r) => r.scan_name || "—",
            },
            {
              key: "scan_type",
              header: "Type",
              render: (r) => r.scan_type || "—",
            },
            {
              key: "tools",
              header: "Tools",
              render: (r) => (r.configured_tools || []).join(", ") || "—",
            },
            {
              key: "status",
              header: "Status",
              render: (r) => <VaptStatusBadge status={r.status} size="sm" />,
            },
            {
              key: "start_time",
              header: "Start Time",
              render: (r) =>
                r.start_time ? new Date(r.start_time).toLocaleString() : "-",
            },
            {
              key: "vulnerabilities_found",
              header: "Findings",
              render: (r) => r.vulnerabilities_found ?? "—",
            },
          ]}
          rows={recentScans}
          onRowClick={(row) => onNavigate && onNavigate("scans", row.scan_id)}
          emptyMessage="No scans yet."
        />
      </div>
    </div>
  );
};

export default VaptDashboard;
